import React, { useMemo, useContext } from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Button, Form, Paragraph } from '@newComponents'
import { YStack, XStack, Stack } from 'tamagui'
import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'
import heartIcon from '@newComponents/EmojiIcon/images/red-heart.png'

import { DialogContext } from '@newComponents/Dialog/Dialog'

import * as Yup from 'yup'

export const GuildDonationAmountForm = ({
  minimumDonationAmount,
  defaultDonationAmount,
  donationCurrency,
  onSubmit,
}: {
  minimumDonationAmount: number
  defaultDonationAmount?: number
  donationCurrency: string
  onSubmit: (donationAmount: number) => Promise<any>
}) => {
  const { t } = useLingui()
  const dialogContext = useContext(DialogContext)

  const validationSchema = useMemo(() => {
    const minimumAmountFormatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: donationCurrency,
      maximumFractionDigits: 0,
    }).format(minimumDonationAmount / 100)

    return Yup.object().shape({
      amount: Yup.number()
        .required(t`Required`)
        .min(
          minimumDonationAmount / 100,
          t`Needs to be at least ${minimumAmountFormatted}`
        ),
    })
  }, [minimumDonationAmount, donationCurrency, t])

  return (
    <>
      <Form
        initialValues={{
          amount: defaultDonationAmount
            ? defaultDonationAmount / 100
            : minimumDonationAmount / 100,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          await onSubmit(values.amount * 100)
        }}
      >
        <Form.Field name='amount' label={t`Donation amount`} flex={1}>
          {dialogContext.open === undefined ? (
            <Stack
              flexDirection='column'
              gap='$2'
              $gtSm={{ flexDirection: 'row', gap: '$8', flex: 1 }}
            >
              <Form.CurrencyInput
                intlConfig={{ locale: 'en-US', currency: donationCurrency }}
                min={minimumDonationAmount / 100}
                // @ts-expect-error size Token conflicts with HTML input size attribute
                size='$6'
                maxWidth='100%'
                flex={1}
              />

              <Form.SubmitButton>
                <Form.SubmitButton.Text>
                  <Trans>Next</Trans>
                </Form.SubmitButton.Text>
              </Form.SubmitButton>
            </Stack>
          ) : (
            <XStack gap='$8' flex={1}>
              <Form.CurrencyInput
                intlConfig={{ locale: 'en-US', currency: donationCurrency }}
                min={minimumDonationAmount / 100}
                // @ts-expect-error size Token conflicts with HTML input size attribute
                size='$6'
                maxWidth='100%'
                flex={1}
              />

              <Form.SubmitButton>
                <Form.SubmitButton.Text>
                  <Trans>Next</Trans>
                </Form.SubmitButton.Text>
              </Form.SubmitButton>
            </XStack>
          )}
        </Form.Field>

        <YStack gap='$2' alignItems='flex-end'>
          <XStack alignItems='center' gap='$2'>
            <Paragraph
              size='$4'
              color='$color'
              textAlign='right'
              maxWidth={200}
            >
              <Trans>Thank you for your support!</Trans>
            </Paragraph>

            <EmojiIcon size='$4' src={heartIcon} />
          </XStack>

          <Paragraph
            size='$4'
            color='$colorSubtle'
            textAlign='right'
            maxWidth={200}
          >
            <Trans>Donations are non-refundable</Trans>
          </Paragraph>
        </YStack>
      </Form>
    </>
  )
}
